import React, { useState, useRef } from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { FileExcel } from 'react-bootstrap-icons';
import { Download } from "@mui/icons-material";
import ExcelJS from "exceljs";
import html2canvas from "html2canvas";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6384'];

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
const darkenColor = (hex, amt = 30) => {
  try {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    const num = parseInt(c, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    r = clamp(r - amt, 0, 255);
    g = clamp(g - amt, 0, 255);
    b = clamp(b - amt, 0, 255);
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch {
    return hex;
  }
};

const PieChartCustom = ({ data, namekey, datakey, title, observaciones }) => {
  const [hiddenItems, setHiddenItems] = useState([]);
  const chartRef = useRef(null);

  // Preparar datos para Plotly 3D Pie
  const chartData = data.map((item) => ({
    name: item[namekey],
    value: item[datakey],
  }));

  const handleLegendClick = (entry) => {
    const itemName = entry.value;
    setHiddenItems((prev) => {
      if (prev.includes(itemName)) {
        return prev.filter((name) => name !== itemName);
      } else {
        return [...prev, itemName];
      }
    });
  };

  const filteredData = chartData.filter((item) => !hiddenItems.includes(item.name));

  const handleSelectAll = () => {
    setHiddenItems([]);
  };

  const handleSelectNone = () => {
    setHiddenItems(chartData.map(item => item.name));
  };

  const labels = filteredData.map((item) => item.name);
  const values = filteredData.map((item) => item.value);
  const baseColors = labels.map((_, i) => COLORS[i % COLORS.length]);
  const borderColors = baseColors.map(c => darkenColor(c, 40));
  const shadowColors = baseColors.map(c => darkenColor(c, 80));

  const plotData = [
    // Capa de sombra para dar relieve (por detrás, ligeramente desplazada)
    {
      type: 'pie',
      labels: labels,
      values: values,
      hole: 0.0,
      marker: {
        colors: shadowColors,
      },
      opacity: 0.25,
      textinfo: 'none',
      hoverinfo: 'skip',
      sort: false,
      direction: 'clockwise',
      domain: { x: [0.02, 1.02], y: [-0.01, 0.99] },
      showlegend: false,
    },
    // Capa principal
    {
      type: 'pie',
      labels: labels,
      values: values,
      hole: 0.0,
      pull: 0.05,
      marker: {
        colors: baseColors,
        line: {
          color: borderColors,
          width: 3
        }
      },
      textinfo: 'label+percent+value',
      textposition: 'outside',
      outsidetextfont: { size: 12, color: '#344767' },
      texttemplate: '%{label}<br>%{percent} (%{value:,.0f})',
      hovertemplate: '<b>%{label}</b><br>' +
                     'Valor: %{value:,.0f}<br>' +
                     'Porcentaje: %{percent}<br>' +
                     '<extra></extra>',
      direction: 'clockwise',
      sort: false,
      domain: { x: [0, 1], y: [0, 1] },
    }
  ];

  const layout = {
    title: {
      text: '',
      font: { size: 18, color: '#344767' }
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    showlegend: false,
    automargin: true,
    uniformtext: { mode: 'show', minsize: 10 },
    margin: { l: 30, r: 30, t: 40, b: 30 },
    height: 520,
  };

  const config = {
    displayModeBar: false,
    displaylogo: false,
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    const total = values.reduce((acc, val) => acc + val, 0);

    const headers = ['Categoría', 'Valor', 'Porcentaje'];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    chartData.forEach((item) => {
      const percentage = ((item.value / total) * 100).toFixed(2) + '%';
      worksheet.addRow([item.name, item.value, percentage]);
    });

    const totalRow = worksheet.addRow(['TOTAL', total, '100%']);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  const handleDownloadPNG = async () => {
    if (chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const link = document.createElement('a');
        link.download = `${title || 'grafico'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error al descargar el gráfico:', error);
      }
    }
  };

  const renderLegend = () => {
    return (
      <MDBox style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '20px' }}>
        <MDBox style={{ display: 'flex', gap: '6px', marginBottom: '8px', borderBottom: '1px solid #e0e0e0', paddingBottom: '6px' }}>
          <MDBox
            onClick={handleSelectAll}
            style={{
              cursor: 'pointer',
              padding: '2px 6px',
              backgroundColor: '#e3f2fd',
              borderRadius: '3px',
              fontSize: '0.65rem',
              color: '#1976d2',
              fontWeight: '500',
            }}
          >
            Todos
          </MDBox>
          <MDBox
            onClick={handleSelectNone}
            style={{
              cursor: 'pointer',
              padding: '2px 6px',
              backgroundColor: '#fce4ec',
              borderRadius: '3px',
              fontSize: '0.65rem',
              color: '#c2185b',
              fontWeight: '500',
            }}
          >
            Ninguno
          </MDBox>
        </MDBox>
        {chartData.map((entry, index) => (
          <MDBox
            key={`legend-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: hiddenItems.includes(entry.name) ? 0.4 : 1,
              transition: 'opacity 0.3s',
            }}
            onClick={() => handleLegendClick({ value: entry.name })}
          >
            <MDBox
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: COLORS[index % COLORS.length],
                marginRight: '6px',
                borderRadius: '2px',
              }}
            />
            <MDTypography variant="caption" fontSize="0.7rem" color="text">
              {entry.name}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
    );
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Download />}
            onClick={handleDownloadPNG}
          >
            PNG
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MDBox style={{ width: '20%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '20px' }}>
          {renderLegend()}
        </MDBox>
        <MDBox 
          style={{ width: '80%' }}
          sx={{ 
            backgroundColor: 'white', 
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
          ref={chartRef}
        >
          <Plot
            data={plotData}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        </MDBox>
      </MDBox>

      {observaciones && (
        <MDTypography variant="caption" color="text" mt={2} style={{ fontStyle: 'italic', opacity: 0.7 }}>
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default PieChartCustom;
